import { sendSegmentConfiguratorViewed } from '../utils/trackingEvents'
import { useProductPageStore } from './ProductPageStore.js'

import React, { useContext, useEffect } from 'react'

import { useRecommendationStore } from '@speedwaymotors/clutch/Stores/RecommendationStore/RecommendationStore.js'
import { useUserStore } from 'Clutch/Stores/UserStore/UserStore'

import createZustandContext from 'Utilities/Zustand/createZustandContext'

import useSegment from 'Hooks/useSegment'

const ConfiguratorContext = createZustandContext(
  ({ initialProps, set, get, props }) => {
    const initialState = {
      ...props.value,
    }

    return {
      ...initialProps,
      ...initialState,

      bustStaticPage: () => {
        import('./fetches/configurator.js').then((f) => {
          f.bustStaticPage(get().skuBaseNumbers, get()._dependencies.webUserGuid).then((data) => {
            set((x) => {
              return {
                ...x,
                configuratorData: [...data],
                isLazyDataHydrated: true,
              }
            })
            get().generateConfiguratorMaps(get().requiredAttributeIds, get().optionalAttributeIds)
            get().generateConfiguratorIndexOptions(get().requiredAttributeIds, get().optionalAttributeIds)
            get().setConfiguratorReviewsAndQuestions(get().configuratorData)
            // TODO: WIFR-33209 More Tracking?
            const segmentData = {
              title: get().title,
              brand: get().brand, // No work
              retailLowPrice: get().retailLowPrice,
              isMadeInUsa: get()._dependencies.isMadeInUsa,
              configuratorPageUrl: get().configuratorPageUrl,
              images: get()._dependencies.images,
              rating: get().rating,
              reviewCount: get().reviewCount,
            }
            sendSegmentConfiguratorViewed(segmentData, get()._dependencies.sendSegmentTrackEventWithName)
          })
        })
      },

      generateConfiguratorMaps: (requiredAttributeIds, optionalAttributeIds) => {
        const configuratorData = get().configuratorData
        configuratorData.forEach((skuBase) => {
          const DIFF_TYPE_ATTR_ID = 593
          const HOUSING_MATERIAL_ATTR_ID = 358
          const RING_GEAR_ATTR_ID = 499

          // Differential Type constants
          const POWERTRAX_LIMITED_SLIP_SKUS = ['91048345', '91048446', '91049350', '91050345', '91049346']
          const POWERTRAX_LIMITED_SLIP_ID = 800001
          const POWERTRAX_LIMITED_SLIP_VALUE = 'PowerTrax Limited Slip'

          const SPEEDWAY_HELICAL_LIMITED_SLIP_SKUS = ['91049349']
          const SPEEDWAY_HELICAL_LIMITED_SLIP_ID = 800002
          const SPEEDWAY_HELICAL_LIMITED_SLIP_VALUE = 'Speedway Helical Gear Limited Slip'

          const SPEEDWAY_TRACK_LOCK_LIMITED_SLIP_SKUS = ['91049345', '91051345']
          const SPEEDWAY_TRACK_LOCK_LIMITED_SLIP_ID = 800003
          const SPEEDWAY_TRACK_LOCK_LIMITED_SLIP_VALUE = 'Speedway Track Lock Limited Slip'

          // Housing Material constants
          const NEW_SPEEDWAY_CASE_SKUS = [
            '91048340',
            '91048341',
            '91048342',
            '91048343',
            '91048345',
            //'91048443', DISCONTINUED?
            '91049345',
            '91049347',
            '91049349',
            '91050345',
            '91051345',
          ]
          const NEW_SPEEDWAY_CASE_ID = 800004
          const NEW_SPEEDWAY_CASE_VALUE = 'Speedway Nodular Iron'

          const CASE_325_SKUS = ['91049348']
          const CASE_325_ID = 800005
          const CASE_325_VALUE = 'Speedway Nodular Iron (35-Spline)'

          const CURRIE_SKUS = ['91048446']
          const CURRIE_ID = 800006
          const CURRIE_VALUE = 'Currie Nodular Iron'

          const OEM_RECONDITIONED_SKUS = ['91049350']
          const OEM_RECONDITIONED_ID = 800007
          const OEM_RECONDITIONED_VALUE = 'OEM Reconditioned'

          const ALUM_CASE_SKUS = ['91049346']
          const ALUM_CASE_ID = 800008
          const ALUM_CASE_VALUE = 'Aluminum'

          // Ring Gear constants
          const PERFORMANCE_RING_GEAR_SKUS = ['91049349', '91049350']
          const PERFORMANCE_RING_GEAR_ID = 800009
          const PERFORMANCE_RING_GEAR_VALUE = 'Performance'

          const YUKON_9_GEAR_SKUS = ['91050345', '91051345']
          const YUKON_9_GEAR_ID = 800010
          const YUKON_9_GEAR_VALUE = 'Yukon 9" Gear'

          // Handle Differential Type HARDCODED translations for specs
          const diffTypeSpec = skuBase.specs.find((spec) => spec.title === 'Differential Type')
          if (diffTypeSpec) {
            if (POWERTRAX_LIMITED_SLIP_SKUS.includes(skuBase.skuBaseNumber)) {
              diffTypeSpec.value = POWERTRAX_LIMITED_SLIP_VALUE
              diffTypeSpec.id = POWERTRAX_LIMITED_SLIP_ID
            } else if (SPEEDWAY_HELICAL_LIMITED_SLIP_SKUS.includes(skuBase.skuBaseNumber)) {
              diffTypeSpec.value = SPEEDWAY_HELICAL_LIMITED_SLIP_VALUE
              diffTypeSpec.id = SPEEDWAY_HELICAL_LIMITED_SLIP_ID
            } else if (SPEEDWAY_TRACK_LOCK_LIMITED_SLIP_SKUS.includes(skuBase.skuBaseNumber)) {
              diffTypeSpec.value = SPEEDWAY_TRACK_LOCK_LIMITED_SLIP_VALUE
              diffTypeSpec.id = SPEEDWAY_TRACK_LOCK_LIMITED_SLIP_ID
            }
          }

          // Handle Housing Material HARDCODED translations for specs
          const housingMaterialSpec = skuBase.specs.find((spec) => spec.title === 'Housing Material')
          if (housingMaterialSpec) {
            if (NEW_SPEEDWAY_CASE_SKUS.includes(skuBase.skuBaseNumber)) {
              housingMaterialSpec.value = NEW_SPEEDWAY_CASE_VALUE
              housingMaterialSpec.id = NEW_SPEEDWAY_CASE_ID
            } else if (CASE_325_SKUS.includes(skuBase.skuBaseNumber)) {
              housingMaterialSpec.value = CASE_325_VALUE
              housingMaterialSpec.id = CASE_325_ID
            } else if (CURRIE_SKUS.includes(skuBase.skuBaseNumber)) {
              housingMaterialSpec.value = CURRIE_VALUE
              housingMaterialSpec.id = CURRIE_ID
            } else if (OEM_RECONDITIONED_SKUS.includes(skuBase.skuBaseNumber)) {
              housingMaterialSpec.value = OEM_RECONDITIONED_VALUE
              housingMaterialSpec.id = OEM_RECONDITIONED_ID
            } else if (ALUM_CASE_SKUS.includes(skuBase.skuBaseNumber)) {
              housingMaterialSpec.value = ALUM_CASE_VALUE
              housingMaterialSpec.id = ALUM_CASE_ID
            }
          }

          // Handle Ring Gear HARDCODED translations for specs
          const ringGearSpec = skuBase.specs.find((spec) => spec.title === 'Ring Gear Weight')
          if (ringGearSpec) {
            if (PERFORMANCE_RING_GEAR_SKUS.includes(skuBase.skuBaseNumber)) {
              ringGearSpec.value = PERFORMANCE_RING_GEAR_VALUE
              ringGearSpec.id = PERFORMANCE_RING_GEAR_ID
            } else if (YUKON_9_GEAR_SKUS.includes(skuBase.skuBaseNumber)) {
              ringGearSpec.value = YUKON_9_GEAR_VALUE
              ringGearSpec.id = YUKON_9_GEAR_ID
            }
          }

          skuBase.skuVariantSummaries.forEach((summary) => {
            summary.skuVariants.forEach((variant) => {
              // Handle Differential Type HARDCODED translations for facetableAttributes
              const diffTypeAttr = variant.facetableAttributes.find((attr) => attr.attributeId === DIFF_TYPE_ATTR_ID)
              if (diffTypeAttr) {
                if (POWERTRAX_LIMITED_SLIP_SKUS.includes(skuBase.skuBaseNumber)) {
                  diffTypeAttr.attributeValue = POWERTRAX_LIMITED_SLIP_VALUE
                  diffTypeAttr.attributeValueId = POWERTRAX_LIMITED_SLIP_ID
                } else if (SPEEDWAY_HELICAL_LIMITED_SLIP_SKUS.includes(skuBase.skuBaseNumber)) {
                  diffTypeAttr.attributeValue = SPEEDWAY_HELICAL_LIMITED_SLIP_VALUE
                  diffTypeAttr.attributeValueId = SPEEDWAY_HELICAL_LIMITED_SLIP_ID
                } else if (SPEEDWAY_TRACK_LOCK_LIMITED_SLIP_SKUS.includes(skuBase.skuBaseNumber)) {
                  diffTypeAttr.attributeValue = SPEEDWAY_TRACK_LOCK_LIMITED_SLIP_VALUE
                  diffTypeAttr.attributeValueId = SPEEDWAY_TRACK_LOCK_LIMITED_SLIP_ID
                }
              }

              // Handle Housing Material HARDCODED translations for facetableAttributes
              const housingMaterialAttr = variant.facetableAttributes.find(
                (attr) => attr.attributeId === HOUSING_MATERIAL_ATTR_ID,
              )
              if (housingMaterialAttr) {
                if (NEW_SPEEDWAY_CASE_SKUS.includes(skuBase.skuBaseNumber)) {
                  housingMaterialAttr.attributeValue = NEW_SPEEDWAY_CASE_VALUE
                  housingMaterialAttr.attributeValueId = NEW_SPEEDWAY_CASE_ID
                } else if (CASE_325_SKUS.includes(skuBase.skuBaseNumber)) {
                  housingMaterialAttr.attributeValue = CASE_325_VALUE
                  housingMaterialAttr.attributeValueId = CASE_325_ID
                } else if (CURRIE_SKUS.includes(skuBase.skuBaseNumber)) {
                  housingMaterialAttr.attributeValue = CURRIE_VALUE
                  housingMaterialAttr.attributeValueId = CURRIE_ID
                } else if (OEM_RECONDITIONED_SKUS.includes(skuBase.skuBaseNumber)) {
                  housingMaterialAttr.attributeValue = OEM_RECONDITIONED_VALUE
                  housingMaterialAttr.attributeValueId = OEM_RECONDITIONED_ID
                } else if (ALUM_CASE_SKUS.includes(skuBase.skuBaseNumber)) {
                  housingMaterialAttr.attributeValue = ALUM_CASE_VALUE
                  housingMaterialAttr.attributeValueId = ALUM_CASE_ID
                }
              }

              // Handle Ring Gear HARDCODED translations for facetableAttributes
              const ringGearAttr = variant.facetableAttributes.find((attr) => attr.attributeId === RING_GEAR_ATTR_ID)
              if (ringGearAttr) {
                if (PERFORMANCE_RING_GEAR_SKUS.includes(skuBase.skuBaseNumber)) {
                  ringGearAttr.attributeValue = PERFORMANCE_RING_GEAR_VALUE
                  ringGearAttr.attributeValueId = PERFORMANCE_RING_GEAR_ID
                } else if (YUKON_9_GEAR_SKUS.includes(skuBase.skuBaseNumber)) {
                  ringGearAttr.attributeValue = YUKON_9_GEAR_VALUE
                  ringGearAttr.attributeValueId = YUKON_9_GEAR_ID
                }
              }
            })
          })
        })

        const allAttributeIds = requiredAttributeIds.concat(optionalAttributeIds)

        // Memoize all this information for later
        // Might make this one computation instead of on indexOption change in the future.
        const attributeIdValueIdsMap = new Map()
        const attributeIdAttributeNameMap = new Map()
        const valueIdValueMap = new Map()
        // For each skuBase
        get().configuratorData.forEach((skuBase) => {
          // For each skuVariantSummary (should just be one skuVariantSummary in there)
          skuBase.skuVariantSummaries.forEach((skuVariantSummary) => {
            // For each skuVariant
            skuVariantSummary.skuVariants.forEach((skuVariant) => {
              // For each facetableAttribute
              skuVariant.facetableAttributes.forEach((facetableAttribute) => {
                const id = facetableAttribute.attributeId

                // Only check relevant attributes
                if (allAttributeIds.includes(id)) {
                  const valueId = facetableAttribute.attributeValueId
                  const name = facetableAttribute.attributeName
                  // Translation layer for value because of phrasing
                  // Could use a map in the future if this wasn't a one off thing
                  const value = valueId === 1227 ? 'OEM reconditioned housing' : facetableAttribute.attributeValue

                  // Add to existing set if it exists otherwise initialize it
                  const existingSet = attributeIdValueIdsMap.get(id)
                  if (existingSet) {
                    existingSet.add(valueId)
                  } else {
                    attributeIdValueIdsMap.set(id, new Set([valueId]))
                    // Set the other 2 maps
                    attributeIdAttributeNameMap.set(id, name)
                  }
                  valueIdValueMap.set(valueId, value)
                }
              })
            })
          })
        })

        set({
          attributeIdValueIdsMap,
          attributeIdAttributeNameMap,
          valueIdValueMap,
        })
      },

      generateConfiguratorIndexOptions: (requiredAttributeIds, optionalAttributeIds) => {
        const indexOptions = get().attributeIdsToIndexOptions(requiredAttributeIds, 0)

        // Do just the required ones for now we will store the optional in configuratorIndexOptions
        set({ indexOptions })

        const configuratorIndexOptions = indexOptions.concat(
          get().attributeIdsToIndexOptions(optionalAttributeIds, indexOptions.length),
        )
        set({ configuratorIndexOptions })
      },

      attributeIdsToIndexOptions: (arr, ordinalityOffset) => {
        // HARDCODED
        const specToIndexOption = new Map([
          ['Axle Splines', 'Spline'],
          ['Ring Gear Weight', 'Ring Gear'],
        ])

        return arr.map((id, index) => {
          const specName = get().attributeIdAttributeNameMap.get(id)
          // Display name gets converted if there is a conversion otherwise uses name from spec
          const displayName = specToIndexOption.get(specName) || specName
          // Ordinality is 1 indexed instead of 0 indexed and ordinalityOffset for optional indexOptions
          const ordinality = index + 1 + ordinalityOffset

          const relevantIndexOptionValueIds = get().attributeIdValueIdsMap.get(id)
          const indexOptionValues = []
          get().valueIdValueMap.forEach((valueDisplayName, valueId) => {
            if (relevantIndexOptionValueIds.has(valueId)) {
              indexOptionValues.push({
                displayName: valueDisplayName,
                id: valueId,
                isDisabled: false,
                isSelected: false,
                ordinality: 0,
              })
            }
          })

          // Handle ordianlity for values alphabetically
          indexOptionValues.sort((a, b) => a.displayName.localeCompare(b.displayName))
          // Ordinality is 1 indexed instead of 0 indexed
          indexOptionValues.forEach((indexOptionValue, index) => (indexOptionValue.ordinality = index + 1))

          return {
            displayName,
            id,
            indexOptionValues,
            ordinality,
          }
        })
      },

      setConfiguratorReviewsAndQuestions: (skuBases) => {
        let rating = 0.0
        let reviewCount = 0
        let numberOfQuestions = 0
        // Get the weighted rating and the total reviewCount
        skuBases.forEach((skuBase) => {
          rating += skuBase.rating * skuBase.reviewCount
          reviewCount += skuBase.reviewCount
          numberOfQuestions += skuBase.numberOfQuestions
        })
        rating /= reviewCount
        set({ rating, reviewCount, numberOfQuestions })
      },

      // Add extra params to have feature parity with PDP
      selectIndexOption: (
        productPageId,
        skuVariant,
        selectedIndexOptions,
        mostRecentlySelectedIndexOption,
        sendFacebookEvent,
      ) => {
        // Adapted from ProductPageStore selectIndexOption and the called onIndexOptionSelection
        get()._dependencies.productPageStoreSet({ loadingIndexOptionSelection: true })
        const configuratorData = get().configuratorData
        const indexOptions = get().indexOptions

        // Convert selectedIndexOptions and mostRecentlySelectedIndexOption to ints for faster processing
        selectedIndexOptions = selectedIndexOptions?.map((x) => parseInt(x))
        mostRecentlySelectedIndexOption = parseInt(mostRecentlySelectedIndexOption)

        // Handle if the user selects an already previously selected indexOption
        if (selectedIndexOptions) {
          for (const [attributeId, attributeValueIds] of get().attributeIdValueIdsMap) {
            const matchingSelectedIndexOptionIndex = selectedIndexOptions?.findIndex((x) => attributeValueIds.has(x))
            const isMostRecentlySelectedIndexOption = attributeValueIds.has(mostRecentlySelectedIndexOption)
            if (matchingSelectedIndexOptionIndex !== -1 && isMostRecentlySelectedIndexOption) {
              // Remove the duplicate indexOption
              selectedIndexOptions.splice(matchingSelectedIndexOptionIndex, 1)

              const duplicateOrdinality = indexOptions.find((indexOption) => indexOption.id === attributeId).ordinality

              // Remove all selected options below the invalidDuplicate
              selectedIndexOptions = selectedIndexOptions?.filter((selectedIndexOption) => {
                // Find the selectedIndexOption's ordinality by looking through all indexOption's indexOptionValues
                const selectedIndexOptionOrdinality = indexOptions.find((indexOption) =>
                  indexOption.indexOptionValues.some((indexOptionValue) => indexOptionValue.id === selectedIndexOption),
                ).ordinality
                // If invalidDuplicate was lower on the page than selectedIndexOption keep it
                return duplicateOrdinality > selectedIndexOptionOrdinality
              })

              break
            }
          }
        }

        // Combine the already selectedIndexOptions and the most recently selected one
        const completeSelectedIndexOptions = (selectedIndexOptions ? selectedIndexOptions : []).concat([
          mostRecentlySelectedIndexOption,
        ])

        let validSkuBases = configuratorData
        let validSkuVariants = []

        // Filtering gets a bit messy with how nested the objects are but here it is
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array
        for (const selectedIndexOption of completeSelectedIndexOptions) {
          // Filter out skuBases without selectedIndexOption in spec
          validSkuBases = validSkuBases.filter((skuBase) => {
            const verified = skuBase.specs.some((spec) => spec.id === selectedIndexOption)
            // If the skuBase is filtered out then add it to be checked as a skuVariant
            if (!verified) {
              skuBase.skuVariantSummaries.forEach((skuVariantSummary) =>
                validSkuVariants.push(...skuVariantSummary.skuVariants),
              )
            }
            return verified
          })
          // Filter out the skuVariants by their facetableAttributes
          validSkuVariants = validSkuVariants.filter((skuVariant) =>
            skuVariant.facetableAttributes.some((attribute) => attribute.attributeValueId === selectedIndexOption),
          )
        }

        const skuVariantNumberList = []
        for (const skuVariant of validSkuVariants) {
          skuVariantNumberList.push(skuVariant.skuVariantNumber)
        }

        // Take the existing indexOptions and recalculate their indexOptionValues
        // HARDCODED TITLE TRANSLATOR
        const indexOptionToSpec = new Map([
          ['Spline', 'Axle Splines'],
          ['Ring Gear', 'Ring Gear Weight'],
        ])

        // Find how many indexOptions to expose to the user
        const requiredAttributesCount = get().requiredAttributeIds?.length || 0
        let selectedIndexOptionsCount = completeSelectedIndexOptions?.length || 0
        // IF there is more than 1 skuVariant remaining
        if (validSkuVariants.length > 1) {
          // Add 1 to allow for the user to facet one over how many they have selected
          selectedIndexOptionsCount += 1
        }
        const indexOptionsCount = Math.max(requiredAttributesCount, selectedIndexOptionsCount)
        let indexOptionsProcessed = 0

        const newIndexOptions = []
        ;(get().configuratorIndexOptions.forEach((indexOption) => {
          // Guard to not show optional indexOptions that we shouldn't
          if (indexOptionsProcessed >= indexOptionsCount) {
            return
          }

          // HARDCODE for Spool Weight
          // If Spool Weight index option
          if (indexOption.id === 594) {
            // If not full spool
            if (!completeSelectedIndexOptions.includes(927)) {
              return
            }
          }

          const indexOptionValueIds = new Set()

          // Handle skuBases
          const title = indexOptionToSpec[indexOption.displayName]
            ? indexOptionToSpec[indexOption.displayName]
            : indexOption.displayName
          // If skuBase doesn't have the indexOption convert it to a skuVariant, otherwise give the value to the set
          validSkuBases = validSkuBases.filter((skuBase) => {
            const spec = skuBase.specs.find((spec) => spec.title === title)
            // If spec is not in skuBase differ to skuVariant for the remainder of the indexOptions
            // (most commonly Gear Ratio)
            if (!spec) {
              skuBase.skuVariantSummaries.forEach((skuVariantSummary) =>
                validSkuVariants.push(...skuVariantSummary.skuVariants),
              )
            } else {
              indexOptionValueIds.add(spec?.id)
            }
            return spec
          })

          // Handle skuVariants
          validSkuVariants.forEach((skuVariant) => {
            const attribute = skuVariant.facetableAttributes.find(
              (attribute) => attribute.attributeId === indexOption.id,
            )
            indexOptionValueIds.add(attribute?.attributeValueId)
          })

          // Handle indexOptionValues - Create new objects to avoid mutation
          let oneIndexOptionSelected = false
          let indexOptionValues = indexOption.indexOptionValues.map((indexOptionValue) => {
            // Set the currently selected indexOption
            const indexOptionSelected = completeSelectedIndexOptions.includes(indexOptionValue.id)
            oneIndexOptionSelected ||= indexOptionSelected

            // Create a new object instead of mutating
            return {
              ...indexOptionValue,
              isSelected: indexOptionSelected,
            }
          })

          // Disable unavailable indexOptions if the dropdown hasn't had a selection yet
          if (!oneIndexOptionSelected) {
            indexOptionValues = indexOptionValues.map((indexOptionValue) => ({
              ...indexOptionValue,
              isDisabled: !indexOptionValueIds.has(indexOptionValue.id),
            }))
          }

          // If this is an optional attribute
          if (indexOptionsProcessed >= requiredAttributesCount) {
            // Count how many indexOptions can be selected
            const availableOptionsCount = indexOptionValues.filter((x) => !x.isDisabled).length
            // And hide the attribute if there is 1 or less
            if (availableOptionsCount <= 1) {
              return
            }
          }

          indexOptionsProcessed++

          newIndexOptions.push({
            ...indexOption,
            indexOptionValues,
          })
        }),
          set((state) => ({
            ...state,
            // Only calculate and show the amount of indexOptions we want to
            indexOptions: newIndexOptions,
          })))
        // First must convert surviving skuBases to skuVariants since price is skuVariant only
        validSkuBases.forEach((skuBase) => {
          skuBase.skuVariantSummaries.forEach((skuVariantSummary) =>
            validSkuVariants.push(...skuVariantSummary.skuVariants),
          )
        })
        validSkuBases = []

        // Find the lowest price to set the starting from price
        let lowestPrice = Math.min(...validSkuVariants.map((validSkuVariant) => validSkuVariant.productPrice.price))
        // Save customer experience on bug
        if (lowestPrice === Infinity) {
          // HARDCODED
          lowestPrice = get().retailLowPrice
        }
        // https://stackoverflow.com/a/17663871
        const price = '$' + lowestPrice.toLocaleString()

        const includedSkuBaseNumbers = new Set()
        validSkuVariants.forEach((validSkuVariant) => includedSkuBaseNumbers.add(validSkuVariant.skuBase.skuBaseNumber))
        const relevantSkuBases = configuratorData.filter((skuBase) => includedSkuBaseNumbers.has(skuBase.skuBaseNumber))
        get().setConfiguratorReviewsAndQuestions(relevantSkuBases)

        // User has selected the starting options
        if (completeSelectedIndexOptions.length >= get().requiredAttributeIds.length) {
          // If one sku remains use it
          if (validSkuVariants.length === 1) {
            const skuVariant = validSkuVariants[0]

            // Batch all ProductPageStore updates into a single call to avoid race conditions
            get()._dependencies.productPageStoreSet({
              isConfigured: true,
              productPageId: skuVariant.skuBase.productPageId,
              skuBaseNumber: skuVariant.skuBase.skuBaseNumber,
              skuVariantNumber: skuVariant.skuVariantNumber,
              price,
              loadingIndexOptionSelection: false,
            })

            get()._dependencies.recommendationsSetSkuBaseNumbers([skuVariant.skuBase.skuBaseNumber])
            get()._dependencies.recommendationsSetSkuVariantNumbers([skuVariant.skuVariantNumber])

            get()._dependencies.setSkuNumbers(skuVariant.skuBase.skuBaseNumber, skuVariant.skuVariantNumber)
            // TODO: WIFR-33209 Is the select the same event as a bust?
            get()._dependencies.productPageBustStaticPage(sendFacebookEvent)
          } else {
            // WIFR-33209 Check if customer selected all indexOptions and still have nothing
            if (
              get().indexOptions.filter((option) => option.indexOptionValues.find((value) => value.isSelected) == null)
                .length === 0
            ) {
              console.error('Configurator out of indexOptions but more than 1 skuVariantLeft', validSkuVariants, get())
            }

            // Only set loading to false if not configured
            get()._dependencies.productPageStoreSet({
              price,
              loadingIndexOptionSelection: false,
            })
          }
        } else {
          // If going from a fully configured to not reset some variables
          get()._dependencies.productPageStoreSet({
            isConfigured: false,
            // productPageId: null,
            // skuBaseNumber: null,
            // skuVariantNumber: null,
            price,
            loadingIndexOptionSelection: false,
            hasPriceRange: true,
            tabs: get().configuratorTabs,
            title: get().configuratorTitle,
            titleH1: get().configuratorTitle,
            breadcrumb: get().configuratorBreadcrumb,
            images: get().configuratorImages,
            productImages: get().configuratorProductImages,
            videos: get().configuratorVideos,
            rebates: null,
            isProp65: null,
            isCARB: null,
            cARBSummary: null,
            cARBDescription: null,
            isCarbAccordionOpen: false,
            digitalAssets: null,
            availability: null,
            bullets: null,
            availabilityDisplayCopy: null,
            strikeThroughPrice: null,
            savingsPrice: null,
            unit: null,
            partNumber: null,
            manufacturerPartNumber: null,
            ribbonText: null,
          })
        }
      },
    }
  },
  {
    name: 'ConfiguratorStore',
    // missingSelectorBehavior: "quiet for now", // "warn",
  },
)

ConfiguratorContext.useSynchronizedState = () => {
  const webUserGuid = useUserStore((x) => x.context.userGuid)
  const productPageStoreSet = useProductPageStore((x) => x.productPageStoreSet)
  const productPageBustStaticPage = useProductPageStore((x) => x.bustStaticPage)
  const isMadeInUsa = useProductPageStore((x) => x.isMadeInUsa)
  const images = useProductPageStore((x) => x.images)
  const recommendationsSetSkuBaseNumbers = useRecommendationStore((x) => x.setSkuBaseNumbers)
  const setSkuNumbers = useRecommendationStore((x) => x.setSkuNumbers)
  const recommendationsSetSkuVariantNumbers = useRecommendationStore((x) => x.setSkuVariantNumbers)
  const { sendSegmentTrackEventWithName } = useSegment()

  return {
    _dependencies: {
      webUserGuid,
      productPageStoreSet,
      productPageBustStaticPage,
      isMadeInUsa,
      images,
      recommendationsSetSkuBaseNumbers,
      recommendationsSetSkuVariantNumbers,
      setSkuNumbers,
      sendSegmentTrackEventWithName,
    },
  }
}

export const useConfiguratorStore = ConfiguratorContext.useStore
export const ConfiguratorProvider = ConfiguratorContext.Provider
